'use client';

import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { useT } from '@/hooks';

import { PALETTE_KEYS } from '@/constants/theme.constants';

interface WithdrawalDetailsProps {
  inAmount: string | null;
  pendingTx: any;
  providerWalletAddress: string | null;
  isWalletMissing: boolean;
  withdrawalId: string | null;
  isProcessing: boolean;
  step: string;
  onSign: () => void;
}

interface WithdrawalActionProps {
  isProcessing: boolean;
  step: string;
  isWalletMissing: boolean;
  withdrawalId: string | null;
  onSign: () => void;
}

const WithdrawalAction = ({
  isProcessing,
  step,
  isWalletMissing,
  withdrawalId,
  onSign,
}: WithdrawalActionProps) => {
  const { t } = useT();

  if (isProcessing) {
    return (
      <Box sx={{ py: 2 }}>
        <CircularProgress size={40} sx={{ color: PALETTE_KEYS.SUCCESS_MAIN, mb: 2 }} />
        <Typography variant="body2" color={PALETTE_KEYS.SUCCESS_MAIN} fontWeight={600}>
          {step === 'loading_tx' && t('withdrawModal.preparingTransaction')}
          {step === 'awaiting_sign' && t('withdrawModal.awaitingWalletSignature')}
          {step === 'confirming' && t('withdrawModal.confirmingOnBlockchain')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {isWalletMissing && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'error.bg',
            border: '1px solid',
            borderColor: 'error.border',
            borderRadius: 1.5,
          }}
        >
          <Typography
            variant="caption"
            color={PALETTE_KEYS.ERROR_MAIN}
            sx={{ display: 'block', textAlign: 'center' }}
          >
            {t('withdrawModal.noEmbeddedWalletError')}
          </Typography>
        </Box>
      )}
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={onSign}
        disabled={isWalletMissing || !withdrawalId}
        sx={{
          py: 2,
          bgcolor: PALETTE_KEYS.SUCCESS_MAIN,
          borderRadius: 2,
          fontWeight: 700,
          fontSize: '1rem',
          '&:hover': { bgcolor: 'success.dark' },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {t('withdrawModal.signAndSendCrypto')}
      </Button>
    </Box>
  );
};

export const WithdrawalDetails = ({
  inAmount,
  pendingTx,
  providerWalletAddress,
  isWalletMissing,
  withdrawalId,
  isProcessing,
  step,
  onSign,
}: WithdrawalDetailsProps) => {
  const { t } = useT();

  const displayAmount =
    inAmount ||
    (pendingTx?.meta?.amount
      ? (Number(pendingTx.meta.amount) / 1000000).toFixed(2)
      : pendingTx?.meta?.inAmount) ||
    '0.00';

  const destinationAddress =
    providerWalletAddress ||
    pendingTx?.meta?.providerWalletAddress ||
    pendingTx?.meta?.toAddress ||
    '---';

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="body1" color={PALETTE_KEYS.TEXT_SECONDARY} sx={{ mb: 4 }}>
        {t('withdrawModal.sellOrderInitiated')}
      </Typography>

      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: 'action.hover',
          borderRadius: 3,
          textAlign: 'left',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color={PALETTE_KEYS.TEXT_SECONDARY}>
            {t('withdrawModal.withdrawAmount')}
          </Typography>
          <Typography variant="h6" color={PALETTE_KEYS.TEXT_PRIMARY} fontWeight={700}>
            {displayAmount} USDC
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color={PALETTE_KEYS.TEXT_SECONDARY}
            sx={{ display: 'block', mb: 0.5 }}
          >
            {t('withdrawModal.destinationAddressOnramper')}
          </Typography>
          <Typography
            variant="body2"
            color={PALETTE_KEYS.TEXT_PRIMARY}
            sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
          >
            {destinationAddress}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color={PALETTE_KEYS.TEXT_SECONDARY}>
            {t('withdrawModal.network')}
          </Typography>
          <Typography variant="body2" color={PALETTE_KEYS.TEXT_PRIMARY}>
            Polygon (PoS)
          </Typography>
        </Box>
      </Box>

      <WithdrawalAction
        isProcessing={isProcessing}
        step={step}
        isWalletMissing={isWalletMissing}
        withdrawalId={withdrawalId}
        onSign={onSign}
      />
    </Box>
  );
};
